import { useState } from "react";
import { InlineSpinner } from "../components/InlineSpinner";
import { TitleEditor } from "../components/TitleEditor";
import { formatDuration, TrackCard } from "../components/TrackCard";
import { SongCover } from "../components/SongCover";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, renameTrack, useWork } from "../lib/api";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";
import { useSongCodes } from "../lib/songCodes";
import { SongCodeBox } from "../components/SongCodeBox";

export function DonePage({ workId }: { workId: string }) {
  const query = useWork(workId);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 곡마다 노래 코드(026). 옛 방식 수업은 고른 곡 한 줄만 온다.
  const { codes: songCodes, reload: reloadSongCodes } = useSongCodes(
    workId,
    query.data ? `${query.data.work.selected_track_id ?? ""}|${query.data.tracks.map((item) => item.id).join(",")}` : "",
  );

  if (query.loading) return <LoadingPage />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "완성한 노래를 불러오지 못했습니다."} />;
  }
  const track = query.data.tracks.find((item) => item.id === query.data?.work.selected_track_id)
    ?? query.data.tracks.at(-1);
  // 불러오기는 끝났는데 곡이 없는 것도 «끝난 실패» 다 — 스피너를 돌리면 영원히 기다리게 된다.
  if (!track) return <ErrorPage message="완성한 노래를 찾지 못했습니다." />;
  const songCode = songCodes.get(track.id) ?? null;
  // 다른 곡도 영상 수업에 가져갈 수 있다 — 곡마다 코드가 따로 있다(2026-10-12 jery).
  const otherCodes = query.data.tracks
    .filter((item) => item.id !== track.id && songCodes.has(item.id))
    .map((item) => ({ track: item, songCode: songCodes.get(item.id)! }));

  const download = async (): Promise<void> => {
    setDownloading(true);
    setError(null);
    try {
      // 🔑 `download=` 를 주면 서버가 «내려받기» 주소(attachment)를 서명한다 — 다른 출처라 `a.download`
      //    만으로는 저장되지 않고 재생 화면으로 넘어갔다(QA03).
      const fileName = `${track.title}.mp3`;
      const result = await apiRequest<{ url: string }>(
        `/api/assets/${encodeURIComponent(track.asset_id)}/url?download=${encodeURIComponent(fileName)}`,
      );
      const anchor = document.createElement("a");
      anchor.href = result.url;
      anchor.download = fileName;
      anchor.rel = "noopener";
      anchor.click();
      setToast("저장을 시작했어요");
      window.setTimeout(() => setToast(null), 2_500);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "노래를 저장하지 못했습니다.");
    } finally {
      setDownloading(false);
    }
  };

  const editLyrics = async (): Promise<void> => {
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "lyrics" }),
      });
      navigate(pathForStep(workId, "lyrics"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "가사 단계로 돌아가지 못했습니다.");
    }
  };

  /** 결과 화면으로 — 다른 곡을 고르거나 스타일을 고치러. 생성 횟수를 쓰지 않는다(2026-10-07 jery). */
  const backToResult = async (): Promise<void> => {
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "result" }),
      });
      navigate(pathForStep(workId, "result"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "이전 단계로 돌아가지 못했습니다.");
    }
  };

  return (
    <StudioShell currentStep={4} remainingSlots={query.data.remaining_slots}>
      <div className="done-page-content">
        <div className="success-icon" aria-hidden="true">✓</div>
        <h1 tabIndex={-1}>노래를 완성했어요</h1>
        <p className="done-song-meta">{track.title} · {formatDuration(track.duration_seconds)}</p>
        <TitleEditor
          label="노래 제목"
          value={track.title}
          onSave={async (title) => {
            try {
              await renameTrack(workId, track.id, title);
            } catch (caught) {
              throw new Error(caught instanceof ApiError ? caught.message : "제목을 저장하지 못했어요.");
            }
            await query.refresh();
          }}
        />
        <TrackCard
          track={track}
          selected
          cover={<SongCover workId={workId} jobId={track.job_id} title={track.title} showSave />}
        />
        {songCode ? (
          <section className="song-code-card">
            <div>
              <strong>뮤직비디오에서 쓸 이 노래의 곡 코드</strong>
              <small>{new Date(songCode.expires_at).toLocaleDateString("ko-KR")}까지 사용할 수 있어요</small>
            </div>
            <code>{songCode.code}</code>
            <button
              className="button-secondary"
              type="button"
              onClick={() => void navigator.clipboard.writeText(songCode.code).then(() => {
                setToast("곡 코드를 복사했어요");
                window.setTimeout(() => setToast(null), 2_500);
              })}
            >복사</button>
          </section>
        ) : null}
        {otherCodes.length > 0 ? (
          <section className="done-other-codes">
            <strong>다른 노래의 곡 코드</strong>
            {otherCodes.map(({ track: other, songCode: code }) => (
              <div key={other.id}>
                <span className="done-other-title">{other.title}</span>
                <SongCodeBox songCode={code} />
              </div>
            ))}
          </section>
        ) : null}
        <div className="done-actions">
          <button className="button-primary" type="button" onClick={() => void download()} disabled={downloading}>
            {downloading ? <InlineSpinner label="저장 준비 중..." /> : "노래 저장 (MP3)"}
          </button>
          <button className="button-secondary" type="button" onClick={() => void editLyrics()} disabled={query.data.remaining_slots <= 0}>
            가사 고쳐서 다시 만들기
          </button>
        </div>
        <div className="step-back-row">
          <button className="button-ghost" type="button" onClick={() => void backToResult()}>← 이전</button>
        </div>
        {query.data.remaining_slots <= 0 ? <p className="limit-guidance centered">남은 생성이 없어요. 선생님께 요청해 주세요.</p> : null}
        <p className="done-footnote">이 노래는 뮤직비디오 수업에서 그대로 쓸 수 있어요</p>
        {error ? <InlineError>{error}</InlineError> : null}
        {toast ? <div className="toast" role="status">{toast} ✓</div> : null}
      </div>
    </StudioShell>
  );
}
